var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { useEventApi } from './useEvent/useEventApi';
import { transformSpaceAPIToSpaceType } from '../SpaceBooking/utils';
/**
 * Suggestion fetches are best-effort: if we can't resolve the full space we
 * simply don't suggest one, so failures must stay silent. A no-op `onError`
 * keeps `fetchSpace` from surfacing the failure to the host (error modal /
 * onError callback).
 */
var SILENT = function () { };
/**
 * Resolves the full {@link Space} for a suggested space id. Owns the detail
 * fetch that used to live inside `SuggestedSpaceContent` so the presentational
 * layer can stay pure and the composer can gate the section on a fully-resolved
 * suggestion (no skeleton flash).
 *
 * Returns `{ suggestedSpace: null }` until the fetch succeeds. A missing space,
 * a failed request, or a settled-but-empty id all resolve to `null`.
 *
 * `suggestionLoading` is the upstream suggested-id query's loading state. While
 * it is `true` the id can briefly drop to `null` (e.g. the query re-runs with
 * new variables when invitees change); we hold the current suggestion across
 * that gap so the space section doesn't collapse, and only clear once the query
 * has settled with no suggestion.
 */
export function useSuggestedSpaceDetails(suggestedSpaceId, suggestionLoading) {
    var _a = useConfig(), accessToken = _a.accessToken, environmentUrls = _a.environmentUrls, tenantId = _a.tenantId, userContext = _a.userContext;
    var currentOrg = userContext.currentOrg;
    var fetchSpace = useEventApi({
        onError: SILENT,
        accessToken: accessToken,
        apiUrl: environmentUrls.apiUrl,
        baseApiUrl: environmentUrls.baseApiUrl,
        tenantId: tenantId,
        currentOrg: currentOrg,
    }).fetchSpace;
    var _b = __read(useState(false), 2), fetching = _b[0], setFetching = _b[1];
    var _c = __read(useState(null), 2), suggestedSpace = _c[0], setSuggestedSpace = _c[1];
    useEffect(function () {
        if (!suggestedSpaceId) {
            // Hold the current suggestion while the id query is still resolving;
            // only clear once it has settled with no suggestion to show.
            if (!suggestionLoading) {
                setSuggestedSpace(null);
                setFetching(false);
            }
            return;
        }
        var cancelled = false;
        setFetching(true);
        // Intentionally do NOT clear `suggestedSpace` here. The id can change while
        // a suggestion is already on screen (e.g. editing invitees re-runs the
        // suggestion query), and blanking it would collapse the space section —
        // which gates `showSection` on a non-null suggestion — until the new fetch
        // resolves. Keep the current card visible and swap once the next one loads.
        fetchSpace({ spaceId: suggestedSpaceId })
            .then(function (space) {
            if (cancelled || !space)
                return;
            setSuggestedSpace(transformSpaceAPIToSpaceType(space));
        })
            .catch(function () {
            // Silent: keep the current suggestion (if any) rather than surfacing a
            // failure or collapsing the section.
        })
            .finally(function () {
            if (!cancelled)
                setFetching(false);
        });
        return function () {
            cancelled = true;
        };
    }, [suggestedSpaceId, suggestionLoading, fetchSpace]);
    return { suggestedSpace: suggestedSpace, fetching: fetching };
}
